import { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const KVM_URL = 'https://kvm.gadoff.net';

declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        initData: string;
        initDataUnsafe: { user?: { id: number; username?: string; first_name: string } };
        ready: () => void;
        expand: () => void;
        close: () => void;
        colorScheme: 'light' | 'dark';
      };
    };
  }
}

const isTelegramWebApp = () => !!(window.Telegram?.WebApp?.initData);

const api = axios.create({ baseURL: '', withCredentials: true });

interface Me       { user_id: number; is_admin: boolean; bot_username: string; }
interface Member   { user_id: number; role: string; display_name: string; joined_at: string; }
interface Invite   { code: string; expires_at: string; used_by: number | null; used_at: string | null; }
interface Note     { id: number; title: string; content: string; created_at: string; scope?: string; owner_id?: number; }
interface ShopItem { id: number; text: string; done: boolean; }
interface Task     { id: number; title: string; category: string; priority: number; deadline: string | null; scope?: string; owner_id?: number; }
interface CalEvent {
  id: number; title: string; date: string; is_yearly: number;
  notify_30: number; notify_7: number; notify_1: number; create_task: number;
  year?: number | null; scope?: string;
}

const MONTHS_RU = ['ЯНВАРЬ','ФЕВРАЛЬ','МАРТ','АПРЕЛЬ','МАЙ','ИЮНЬ','ИЮЛЬ','АВГУСТ','СЕНТЯБРЬ','ОКТЯБРЬ','НОЯБРЬ','ДЕКАБРЬ'];
const MONTHS_RU_GEN = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
const WEEKDAYS = ['ПН','ВТ','СР','ЧТ','ПТ','СБ','ВС'];

const PRIORITY: Record<number, { label: string; color: string }> = {
  1: { label: 'Высокий', color: '#ff4444' },
  2: { label: 'Средний', color: '#ffaa44' },
  3: { label: 'Низкий',  color: '#33ff33' },
};

const BOOT_LINES: { text: string; cls: string }[] = [
  { text: 'VAULT-TEC INDUSTRIES UNIFIED OPERATING SYSTEM', cls: '' },
  { text: 'COPYRIGHT 2075-2077 VAULT-TEC CORP.', cls: 'dim' },
  { text: '', cls: '' },
  { text: 'CAUTION: UNAUTHORIZED ACCESS IS PROHIBITED', cls: 'warn' },
  { text: 'VIOLATORS WILL BE PROSECUTED TO FULL EXTENT OF VAULT LAW', cls: 'dim' },
  { text: '', cls: '' },
  { text: '> RUNNING SELF-DIAGNOSTICS..........', cls: '' },
  { text: '> MEMORY SUBSYSTEM...................OK', cls: 'ok' },
  { text: '> NETWORK INTERFACE..................OK', cls: 'ok' },
  { text: '> FAMILY DATABASE....................OK', cls: 'ok' },
  { text: '> AUTHENTICATION MODULE..............OK', cls: 'ok' },
  { text: '', cls: '' },
  { text: '> VAULT FAMILY SYSTEM v2.4 INITIALIZED', cls: 'ok' },
  { text: '', cls: '' },
];

const MENU_ITEMS = [
  { label: 'ВОЙТИ В СИСТЕМУ', sub: '/login в Telegram → перейдите по ссылке', action: 'login' },
  { label: 'TERMINAL ARCADE  →', sub: 'kvm.gadoff.net', action: 'kvm' },
];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}
function formatDeadline(iso: string) {
  return new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
const isOverdue = (iso: string | null) => !!iso && new Date(iso) < new Date();

function daysUntilEvent(ev: CalEvent): number {
  const today = new Date(); today.setHours(0,0,0,0);
  let occ: Date;
  if (ev.is_yearly) {
    const [mm, dd] = ev.date.split('-').map(Number);
    occ = new Date(today.getFullYear(), mm - 1, dd);
    if (occ < today) occ = new Date(today.getFullYear() + 1, mm - 1, dd);
  } else {
    const [y, mm, dd] = ev.date.split('-').map(Number);
    occ = new Date(y, mm - 1, dd);
  }
  return Math.round((occ.getTime() - today.getTime()) / 86400000);
}

function formatEventDate(ev: CalEvent): string {
  if (ev.is_yearly) {
    const [mm, dd] = ev.date.split('-').map(Number);
    return `${dd} ${MONTHS_RU_GEN[mm - 1]}`;
  }
  const [y, mm, dd] = ev.date.split('-').map(Number);
  return `${dd} ${MONTHS_RU_GEN[mm - 1]} ${y}`;
}

function occurrenceYear(ev: CalEvent): number {
  const today = new Date(); today.setHours(0,0,0,0);
  if (ev.is_yearly) {
    const [mm, dd] = ev.date.split('-').map(Number);
    const occ = new Date(today.getFullYear(), mm - 1, dd);
    return occ < today ? today.getFullYear() + 1 : today.getFullYear();
  }
  return Number(ev.date.split('-')[0]);
}

function getEventAge(ev: CalEvent): string {
  if (!ev.year) return '';
  const occ_year = occurrenceYear(ev);
  const n = occ_year - ev.year;
  if (n <= 0) return '';
  const r = n % 10, rc = n % 100;
  const word = (11 <= rc && rc <= 14) || r === 0 || r >= 5 ? 'лет'
    : r === 1 ? 'год' : 'года';
  return `${n} ${word}`;
}

function eventsForDay(events: CalEvent[], year: number, month: number, day: number): CalEvent[] {
  return events.filter(ev => {
    if (ev.is_yearly) {
      const [mm, dd] = ev.date.split('-').map(Number);
      return mm === month + 1 && dd === day;
    }
    const [y, mm, dd] = ev.date.split('-').map(Number);
    return y === year && mm === month + 1 && dd === day;
  });
}

function App() {
  // ── Auth ──
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null);

  // ── Login terminal ──
  const [loginPhase, setLoginPhase] = useState<'init'|'booting'|'menu'>('init');
  const [bootLines, setBootLines] = useState<{text:string,cls:string}[]>([]);
  const [menuSel, setMenuSel] = useState(0);
  const [showLoginHint, setShowLoginHint] = useState(false);

  // ── Nixie clock ──
  const [nixieTime, setNixieTime] = useState(() => {
    const n = new Date();
    return String(n.getHours()).padStart(2,'0') + String(n.getMinutes()).padStart(2,'0');
  });
  const [nixieBlink, setNixieBlink] = useState(true);

  // ── Family ──
  const [me, setMe]             = useState<Me | null>(null);
  const [members, setMembers]   = useState<Member[]>([]);
  const [invites, setInvites]   = useState<Invite[]>([]);
  const [newInviteLink, setNewInviteLink] = useState('');
  const [familyError, setFamilyError]     = useState('');

  // ── Data ──
  const [notes, setNotes]           = useState<Note[]>([]);
  const [shopItems, setShopItems]   = useState<ShopItem[]>([]);
  const [personalShopItems, setPersonalShopItems] = useState<ShopItem[]>([]);
  const [tasks, setTasks]           = useState<Task[]>([]);
  const [events, setEvents]         = useState<CalEvent[]>([]);

  const [newNote, setNewNote]   = useState({ title: '', content: '', scope: 'family' as 'family'|'personal' });
  const [newShopItem, setNewShopItem] = useState('');
  const [newPersonalShopItem, setNewPersonalShopItem] = useState('');
  const [newTask, setNewTask]   = useState('');
  const [newTaskDeadline, setNewTaskDeadline] = useState<Date | null>(null);
  const [newTaskPriority, setNewTaskPriority] = useState(2);
  const [newTaskScope, setNewTaskScope] = useState<'family'|'personal'>('family');
  const [eventsExpanded, setEventsExpanded] = useState(false);

  // ── Calendar ──
  const today = new Date();
  const [calYear, setCalYear]   = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [showEventForm, setShowEventForm] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '', date: '', is_yearly: true,
    notify_30: true, notify_7: true, notify_1: true, create_task: false,
    year: '' as number | '', scope: 'family' as 'family'|'personal',
  });
  const [eventDateRaw, setEventDateRaw] = useState('');
  const [eventError, setEventError] = useState('');

  // ── Shopping item split ──
  const [splittingItemId, setSplittingItemId] = useState<number | null>(null);
  const [splitText, setSplitText] = useState('');
  const [splitIsPersonal, setSplitIsPersonal] = useState(false);

  const saveSplit = async () => {
    if (!splittingItemId) return;
    const parts = splitText.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const addUrl = splitIsPersonal ? '/api/shopping/personal' : '/api/shopping';
    const delUrl = splitIsPersonal ? `/api/shopping/personal/${splittingItemId}` : `/api/shopping/${splittingItemId}`;
    for (const p of parts) await api.post(addUrl, { text: p });
    await api.delete(delUrl);
    setSplittingItemId(null); setSplitText('');
    loadData();
  };

  // ── Note/Task inline edit ──
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editNote, setEditNote] = useState({ title: '', content: '' });
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editTask, setEditTask] = useState({ title: '', priority: 2, deadline: null as Date | null });

  // ── Event edit ──
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    title: '', date: '', is_yearly: true,
    notify_30: true, notify_7: true, notify_1: true, create_task: false,
    year: '' as number | '',
  });
  const [editDateRaw, setEditDateRaw] = useState('');
  const [editError, setEditError] = useState('');

  // ── Nixie clock ticker ──
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setNixieTime(String(n.getHours()).padStart(2,'0') + String(n.getMinutes()).padStart(2,'0'));
      setNixieBlink(b => !b);
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // ── Boot animation ──
  useEffect(() => {
    if (loginPhase !== 'booting') return;
    let cancelled = false;
    let delay = 0;
    const acc: {text:string,cls:string}[] = [];
    BOOT_LINES.forEach((line, i) => {
      delay += line.text ? 60 + line.text.length * 6 : 55;
      setTimeout(() => {
        if (cancelled) return;
        acc.push(line);
        setBootLines([...acc]);
        if (i === BOOT_LINES.length - 1) {
          setTimeout(() => { if (!cancelled) setLoginPhase('menu'); }, 400);
        }
      }, delay);
    });
    return () => { cancelled = true; };
  }, [loginPhase]);

  // ── Menu keyboard navigation ──
  useEffect(() => {
    if (loggedIn !== false || loginPhase !== 'menu') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') { e.preventDefault(); setMenuSel(s => (s - 1 + MENU_ITEMS.length) % MENU_ITEMS.length); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setMenuSel(s => (s + 1) % MENU_ITEMS.length); }
      else if (e.key === 'Enter') { handleMenuSelect(menuSel); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [loggedIn, loginPhase, menuSel]); // eslint-disable-line react-hooks/exhaustive-deps

  const startBoot = () => { setLoginPhase('booting'); setBootLines([]); };

  const handleMenuSelect = (idx: number) => {
    if (MENU_ITEMS[idx].action === 'kvm') window.open(KVM_URL, '_blank');
    else setShowLoginHint(h => !h);
  };

  // ── Data loading ──
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
      document.body.classList.add('tg-app');
    }
    api.get('/api/notes')
      .then(r => { setNotes(r.data); setLoggedIn(true); })
      .catch(async () => {
        if (isTelegramWebApp()) {
          try {
            await api.post('/api/auth/telegram', { initData: window.Telegram!.WebApp.initData });
            const r = await api.get('/api/notes');
            setNotes(r.data);
            setLoggedIn(true);
          } catch {
            setLoggedIn(false);
          }
        } else {
          setLoggedIn(false);
        }
      });
  }, []);

  useEffect(() => {
    if (!loggedIn) return;
    api.get('/api/me').then(r => setMe(r.data)).catch(console.error);
  }, [loggedIn]);

  useEffect(() => {
    if (!me) return;
    api.get('/api/family/members').then(r => setMembers(r.data)).catch(console.error);
    if (me.is_admin) {
      api.get('/api/family/invites').then(r => setInvites(r.data)).catch(console.error);
    }
  }, [me]);

  const loadFamily = () => {
    api.get('/api/family/members').then(r => setMembers(r.data)).catch(console.error);
    if (me?.is_admin) {
      api.get('/api/family/invites').then(r => setInvites(r.data)).catch(console.error);
    }
  };

  const loadData = () => {
    api.get('/api/notes').then(r => setNotes(r.data)).catch(console.error);
    api.get('/api/shopping').then(r => setShopItems(r.data)).catch(console.error);
    api.get('/api/shopping/personal').then(r => setPersonalShopItems(r.data)).catch(console.error);
    api.get('/api/tasks').then(r => setTasks(r.data)).catch(console.error);
    api.get('/api/events').then(r => setEvents(r.data)).catch(console.error);
  };

  useEffect(() => { if (loggedIn) loadData(); }, [loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──
  const createInvite = async () => {
    setFamilyError(''); setNewInviteLink('');
    try {
      const r = await api.post('/api/family/invites');
      setNewInviteLink(r.data.link || `https://t.me/${me?.bot_username}?start=${r.data.code}`);
      loadFamily();
    } catch (e: any) {
      setFamilyError(e?.response?.data?.detail ?? 'Ошибка создания инвайта');
    }
  };

  const revokeInvite = async (code: string) => {
    await api.delete(`/api/family/invites/${code}`).catch(console.error);
    setNewInviteLink(''); loadFamily();
  };

  const removeMember = async (userId: number) => {
    if (!window.confirm('Удалить участника из семьи?')) return;
    await api.delete(`/api/family/members/${userId}`).catch(console.error);
    loadFamily();
  };

  const handleLogout = async () => {
    await api.post('/api/logout').catch(console.error);
    setLoggedIn(false);
    setLoginPhase('init');
    setShowLoginHint(false);
  };

  const addNote = async () => {
    if (!newNote.title.trim()) return;
    await api.post('/api/notes', newNote);
    setNewNote({ title: '', content: '', scope: 'family' }); loadData();
  };

  const saveNote = async (id: number) => {
    if (!editNote.title.trim()) return;
    await api.put(`/api/notes/${id}`, { title: editNote.title, content: editNote.content });
    setEditingNoteId(null); loadData();
  };

  const saveTask = async (id: number) => {
    if (!editTask.title.trim()) return;
    await api.put(`/api/tasks/${id}`, {
      title: editTask.title,
      priority: editTask.priority,
      deadline_utc: editTask.deadline ? editTask.deadline.toISOString() : null,
    });
    setEditingTaskId(null); loadData();
  };

  const addShopItem = async () => {
    if (!newShopItem.trim()) return;
    await api.post('/api/shopping', { text: newShopItem });
    setNewShopItem(''); loadData();
  };

  const addPersonalShopItem = async () => {
    if (!newPersonalShopItem.trim()) return;
    await api.post('/api/shopping/personal', { text: newPersonalShopItem });
    setNewPersonalShopItem(''); loadData();
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    await api.post('/api/tasks', {
      title: newTask, category: 'general',
      priority: newTaskPriority,
      deadline: newTaskDeadline ? newTaskDeadline.toISOString() : null,
      scope: newTaskScope,
    });
    setNewTask(''); setNewTaskDeadline(null); setNewTaskPriority(2); setNewTaskScope('family'); loadData();
  };

  const addEvent = async () => {
    setEventError('');
    if (!newEvent.title.trim()) { setEventError('Введите название'); return; }
    if (!newEvent.date) { setEventError('Выберите дату'); return; }
    try {
      await api.post('/api/events', {
        title: newEvent.title, date: newEvent.date,
        is_yearly: newEvent.is_yearly ? 1 : 0,
        notify_30: newEvent.notify_30 ? 1 : 0,
        notify_7: newEvent.notify_7 ? 1 : 0,
        notify_1: newEvent.notify_1 ? 1 : 0,
        create_task: newEvent.create_task ? 1 : 0,
        year: newEvent.year !== '' ? Number(newEvent.year) : null,
        scope: newEvent.scope,
      });
      setNewEvent({ title:'', date:'', is_yearly:true, notify_30:true, notify_7:true, notify_1:true, create_task:false, year:'', scope:'family' });
      setEventDateRaw(''); setShowEventForm(false); loadData();
    } catch (e: any) {
      setEventError(e?.response?.data?.detail ?? 'Ошибка сохранения');
    }
  };

  const startEdit = (ev: CalEvent) => {
    const thisYear = new Date().getFullYear();
    const raw = ev.is_yearly ? `${thisYear}-${ev.date}` : ev.date;
    setEditingId(ev.id); setEditDateRaw(raw);
    setEditForm({
      title: ev.title, date: ev.date, is_yearly: !!ev.is_yearly,
      notify_30: !!ev.notify_30, notify_7: !!ev.notify_7, notify_1: !!ev.notify_1,
      create_task: !!ev.create_task, year: ev.year ?? '',
    });
    setEditError('');
  };

  const handleEditDateChange = (raw: string, overrideYearly?: boolean) => {
    setEditDateRaw(raw);
    if (!raw) { setEditForm(f => ({ ...f, date: '' })); return; }
    setEditForm(f => {
      const yearly = overrideYearly !== undefined ? overrideYearly : f.is_yearly;
      const parts = raw.split('-');
      return { ...f, date: yearly ? `${parts[1]}-${parts[2]}` : raw };
    });
  };

  const saveEdit = async (id: number) => {
    setEditError('');
    if (!editForm.title.trim()) { setEditError('Введите название'); return; }
    if (!editForm.date) { setEditError('Выберите дату'); return; }
    try {
      await api.put(`/api/events/${id}`, {
        title: editForm.title, date: editForm.date,
        is_yearly: editForm.is_yearly ? 1 : 0,
        notify_30: editForm.notify_30 ? 1 : 0,
        notify_7: editForm.notify_7 ? 1 : 0,
        notify_1: editForm.notify_1 ? 1 : 0,
        create_task: editForm.create_task ? 1 : 0,
        year: editForm.year !== '' ? Number(editForm.year) : null,
      });
      setEditingId(null); loadData();
    } catch (e: any) {
      setEditError(e?.response?.data?.detail ?? 'Ошибка сохранения');
    }
  };

  const handleEventDateChange = (raw: string, overrideYearly?: boolean) => {
    setEventDateRaw(raw);
    if (!raw) { setNewEvent(e => ({ ...e, date: '' })); return; }
    setNewEvent(e => {
      const yearly = overrideYearly !== undefined ? overrideYearly : e.is_yearly;
      const parts = raw.split('-');
      return { ...e, date: yearly ? `${parts[1]}-${parts[2]}` : raw };
    });
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  // ── Calendar grid ──
  const todayStr = today.toLocaleDateString('ru-RU', { weekday:'long', day:'numeric', month:'long' });
  const firstDayJS = new Date(calYear, calMonth, 1).getDay();
  const firstDayMon = (firstDayJS + 6) % 7;
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  const calCells: (number | null)[] = [];
  for (let i = 0; i < firstDayMon; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);
  const sortedEvents = [...events].sort((a, b) => daysUntilEvent(a) - daysUntilEvent(b));

  return (
    <div className="room">
      <div className="wall-glow" />
      <div className="monitor">

        <div className="top-bezel">
          <span className="model-label">VAULT-TEC CORP · FAMILY TERMINAL v2.4</span>
        </div>

        <div className="screen-bezel">
          <div className="screen-wrap">
            <div className="scan-beam" />
            <div className="screen-glare" />
            <div className={`screen${loggedIn ? ' screen--app' : ''}`}>

              {/* Loading */}
              {loggedIn === null && (
                <div className="term-center">
                  <div className="boot-logo">⚙ VAULT</div>
                  <div className="boot-line">ИНИЦИАЛИЗАЦИЯ...</div>
                </div>
              )}

              {/* Login terminal */}
              {loggedIn === false && (
                <>
                  {loginPhase === 'init' && (
                    <div className="term-init" onClick={startBoot}>
                      <div className="init-logo">
                        VAULT-TEC INDUSTRIES (TM)<br/>
                        UNIFIED OPERATING SYSTEM<br/>
                        COPYRIGHT 2075-2077
                      </div>
                      <div className="init-prompt">▶ НАЖМИТЕ ДЛЯ ИНИЦИАЛИЗАЦИИ ТЕРМИНАЛА</div>
                    </div>
                  )}

                  {loginPhase === 'booting' && (
                    <div className="term-boot">
                      {bootLines.map((line, i) => (
                        <div key={i} className={`bl${line.cls ? ' bl-' + line.cls : ''}`}>
                          {line.text || ' '}
                        </div>
                      ))}
                      <span className="cblk" />
                    </div>
                  )}

                  {loginPhase === 'menu' && (
                    <div className="term-menu">
                      <div className="msys">
                        <div>&gt; VAULT-TEC FAMILY SYSTEM v2.4</div>
                        <div>&gt; TERMINAL READY · {new Date().toLocaleTimeString('ru-RU', {hour:'2-digit',minute:'2-digit'})}</div>
                      </div>
                      <div className="mhead">ВЫБЕРИТЕ ДЕЙСТВИЕ</div>
                      <div className="mlist">
                        {MENU_ITEMS.map((item, i) => (
                          <div key={i}
                            className={`mi${menuSel === i ? ' sel' : ''}`}
                            onMouseEnter={() => setMenuSel(i)}
                            onClick={() => handleMenuSelect(i)}
                          >
                            <span className="arr">▶</span>
                            <span className="gn">{item.label}</span>
                            <span className="gk">{item.sub}</span>
                          </div>
                        ))}
                      </div>
                      {showLoginHint && (
                        <div className="login-hint">
                          <div className="lh-line">&gt; ИНСТРУКЦИЯ ДОСТУПА:</div>
                          <div className="lh-line lh-dim">1. Откройте Telegram</div>
                          <div className="lh-line lh-dim">2. Напишите боту команду /login</div>
                          <div className="lh-line lh-dim">3. Перейдите по полученной ссылке</div>
                          <div className="lh-line lh-ok">&gt; АВТОРИЗАЦИЯ ЧЕРЕЗ TELEGRAM SECURE LINK ■</div>
                        </div>
                      )}
                      <hr className="msep" />
                      <div className="mfoot">↑↓ НАВИГАЦИЯ &nbsp;·&nbsp; ENTER ВЫБОР &nbsp;·&nbsp; MOUSE CLICK</div>
                    </div>
                  )}
                </>
              )}

              {/* App */}
              {loggedIn && (
                <div className="app">
                  <header className="topbar">
                    <div className="topbar-brand">
                      <span className="topbar-logo">⚙</span>
                      <span className="topbar-title">VAULT</span>
                    </div>
                    <span className="topbar-date">{todayStr.toUpperCase()}</span>
                    <button className="btn-logout" onClick={handleLogout}>[ВЫХОД]</button>
                  </header>

                  <main className="content">

                    {/* ── TOP ROW: Покупки + Мини-календарь ── */}
                    <div className="top-row">
                      <div className="shop-panels">
                        {/* Семейный список */}
                        <section className="panel panel--shop">
                          <div className="section-header">
                            <span className="section-title">▸ СЕМЕЙНЫЙ СПИСОК</span>
                            <span className="section-count">[{shopItems.filter(i => !i.done).length}]</span>
                            {shopItems.some(i => i.done) && (
                              <button className="btn btn--sm btn--dim" style={{marginLeft:'auto'}}
                                onClick={() => api.delete('/api/shopping/done/all').then(loadData)}>
                                [🗑 ОТМЕЧЕННЫЕ]
                              </button>
                            )}
                          </div>
                          <div className="add-form-row">
                            <input className="input" placeholder="ДОБАВИТЬ ТОВАР"
                              value={newShopItem}
                              onChange={e => setNewShopItem(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && addShopItem()} />
                            <button className="btn-icon" onClick={addShopItem}>[+]</button>
                          </div>
                          {shopItems.map(item => (
                            <div key={item.id} className={`shop-item${item.done ? ' shop-item--done' : ''}${splittingItemId === item.id ? ' shop-item--splitting' : ''}`}>
                              {splittingItemId === item.id ? (
                                <div className="shop-split-form">
                                  <textarea className="input shop-split-input" value={splitText}
                                    onChange={e => setSplitText(e.target.value)}
                                    placeholder={"каждая строка или через запятую — отдельный товар"} rows={3} />
                                  <div className="shop-split-actions">
                                    <button className="btn" onClick={saveSplit}>[✓ РАЗДЕЛИТЬ]</button>
                                    <button className="btn" style={{color:'var(--text-dim)'}} onClick={() => setSplittingItemId(null)}>[отмена]</button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <button className={`shop-check${item.done ? ' checked' : ''}`}
                                    onClick={() => api.put(`/api/shopping/${item.id}/toggle`).then(loadData)}>
                                    {item.done ? '[✓]' : '[ ]'}
                                  </button>
                                  <span className="shop-text">{item.text}</span>
                                  <button className="btn-split" title="Разделить на позиции" onClick={() => { setSplittingItemId(item.id); setSplitText(item.text); setSplitIsPersonal(false); }}>[÷]</button>
                                  <button className="btn-move" title="В личный список" onClick={() => api.put(`/api/shopping/${item.id}/move`).then(loadData)}>→Л</button>
                                  <button className="btn-delete" onClick={() => api.delete(`/api/shopping/${item.id}`).then(loadData)}>[×]</button>
                                </>
                              )}
                            </div>
                          ))}
                          {shopItems.length === 0 && <div className="empty">[ ПУСТО ]</div>}
                        </section>

                        {/* Личный список */}
                        <section className="panel panel--shop">
                          <div className="section-header">
                            <span className="section-title">▸ ЛИЧНЫЙ СПИСОК</span>
                            <span className="section-count">[{personalShopItems.filter(i => !i.done).length}]</span>
                            {personalShopItems.some(i => i.done) && (
                              <button className="btn btn--sm btn--dim" style={{marginLeft:'auto'}}
                                onClick={() => api.delete('/api/shopping/personal/done/all').then(loadData)}>
                                [🗑 ОТМЕЧЕННЫЕ]
                              </button>
                            )}
                          </div>
                          <div className="add-form-row">
                            <input className="input" placeholder="ДОБАВИТЬ ТОВАР"
                              value={newPersonalShopItem}
                              onChange={e => setNewPersonalShopItem(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && addPersonalShopItem()} />
                            <button className="btn-icon" onClick={addPersonalShopItem}>[+]</button>
                          </div>
                          {personalShopItems.map(item => (
                            <div key={item.id} className={`shop-item${item.done ? ' shop-item--done' : ''}${splittingItemId === item.id ? ' shop-item--splitting' : ''}`}>
                              {splittingItemId === item.id ? (
                                <div className="shop-split-form">
                                  <textarea className="input shop-split-input" value={splitText}
                                    onChange={e => setSplitText(e.target.value)}
                                    placeholder={"каждая строка или через запятую — отдельный товар"} rows={3} />
                                  <div className="shop-split-actions">
                                    <button className="btn" onClick={saveSplit}>[✓ РАЗДЕЛИТЬ]</button>
                                    <button className="btn" style={{color:'var(--text-dim)'}} onClick={() => setSplittingItemId(null)}>[отмена]</button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <button className={`shop-check${item.done ? ' checked' : ''}`}
                                    onClick={() => api.put(`/api/shopping/personal/${item.id}/toggle`).then(loadData)}>
                                    {item.done ? '[✓]' : '[ ]'}
                                  </button>
                                  <span className="shop-text">{item.text}</span>
                                  <button className="btn-split" title="Разделить на позиции" onClick={() => { setSplittingItemId(item.id); setSplitText(item.text); setSplitIsPersonal(true); }}>[÷]</button>
                                  <button className="btn-move" title="В семейный список" onClick={() => api.put(`/api/shopping/personal/${item.id}/move`).then(loadData)}>→С</button>
                                  <button className="btn-delete" onClick={() => api.delete(`/api/shopping/personal/${item.id}`).then(loadData)}>[×]</button>
                                </>
                              )}
                            </div>
                          ))}
                          {personalShopItems.length === 0 && <div className="empty">[ ПУСТО ]</div>}
                        </section>
                      </div>

                      {/* Мини-календарь */}
                      <div className="cal-wrap cal-wrap--mini">
                        <div className="cal-nav">
                          <button className="cal-nav-btn" onClick={prevMonth}>◄</button>
                          <span className="cal-month-label">{MONTHS_RU[calMonth]} {calYear}</span>
                          <button className="cal-nav-btn" onClick={nextMonth}>►</button>
                        </div>
                        <div className="cal-grid">
                          {WEEKDAYS.map(wd => <div key={wd} className="cal-header">{wd}</div>)}
                          {calCells.map((day, i) => {
                            if (day === null) return <div key={`e${i}`} className="cal-cell cal-cell--empty" />;
                            const dateKey = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                            const isToday = dateKey === todayKey;
                            const dayEvs = eventsForDay(events, calYear, calMonth, day);
                            return (
                              <div key={day}
                                className={`cal-cell${isToday ? ' cal-cell--today' : ''}${dayEvs.length > 0 ? ' cal-cell--has-event' : ''}`}
                                title={dayEvs.map(e => e.title).join(', ')}
                                onClick={() => {
                                  if (dayEvs.length > 0) {
                                    document.getElementById('section-events')?.scrollIntoView({ behavior: 'smooth' });
                                  } else {
                                    setShowEventForm(true);
                                    const y=calYear, m=String(calMonth+1).padStart(2,'0'), d=String(day).padStart(2,'0');
                                    setEventDateRaw(`${y}-${m}-${d}`);
                                    handleEventDateChange(`${y}-${m}-${d}`);
                                    document.getElementById('section-events')?.scrollIntoView({ behavior: 'smooth' });
                                  }
                                }}>
                                <span className="cal-day-num">{day}</span>
                                {dayEvs.length > 0 && <span className="cal-dot" />}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* ── ЗАДАЧИ ── */}
                    <section className="section">
                      <div className="section-header">
                        <span className="section-title">▸ ЗАДАЧИ</span>
                        <span className="section-count">[{tasks.filter(t => t.category !== 'done').length}]</span>
                      </div>
                      <div className="add-form task-add-form">
                        <input className="input" placeholder="НОВАЯ ЗАДАЧА"
                          value={newTask}
                          onChange={e => setNewTask(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addTask()} />
                        <DatePicker
                          selected={newTaskDeadline}
                          onChange={(date: Date | null) => setNewTaskDeadline(date)}
                          showTimeSelect dateFormat="dd.MM HH:mm"
                          placeholderText="ДЕДЛАЙН" className="input input--date"
                          timeFormat="HH:mm" timeIntervals={15} />
                        <select className="input input--select input--priority"
                          value={newTaskPriority}
                          onChange={e => setNewTaskPriority(Number(e.target.value))}>
                          <option value={1}>!! ВЫСОКИЙ</option>
                          <option value={2}>!  СРЕДНИЙ</option>
                          <option value={3}>   НИЗКИЙ</option>
                        </select>
                        <label className="check-label">
                          <input type="checkbox" checked={newTaskScope === 'personal'}
                            onChange={e => setNewTaskScope(e.target.checked ? 'personal' : 'family')} />
                          ЛИЧНОЕ
                        </label>
                        <button className="btn-icon" onClick={addTask}>[+]</button>
                      </div>
                      <div className="kanban">
                        {[
                          { key: 'general',     label: 'ОЧЕРЕДЬ' },
                          { key: 'in_progress', label: 'В РАБОТЕ' },
                          { key: 'done',        label: 'ГОТОВО' },
                        ].map(col => (
                          <div key={col.key} className="kanban-col">
                            <div className="kanban-col-title">
                              {col.label}
                              <span className="kanban-col-count">
                                [{tasks.filter(t => t.category === col.key).length}]
                              </span>
                              {col.key === 'done' && tasks.some(t => t.category === 'done') && (
                                <button className="btn btn--sm btn--dim"
                                  onClick={() => api.delete('/api/tasks/done/all').then(loadData)}>
                                  [🗑]
                                </button>
                              )}
                            </div>
                            {tasks.filter(t => t.category === col.key).map(t => {
                              const prio = PRIORITY[t.priority] ?? PRIORITY[2];
                              const overdue = isOverdue(t.deadline) && col.key !== 'done';
                              return (
                                <div key={t.id} className={`kanban-card${overdue ? ' kanban-card--overdue' : ''}${t.scope === 'personal' ? ' kanban-card--personal' : ''}`}>
                                  <div className="kanban-card-top">
                                    <span className="priority-tag" style={{ color: prio.color }}>{prio.label.toUpperCase()}</span>
                                    <span style={{display:'flex',gap:'4px',alignItems:'center'}}>
                                      {t.scope === 'personal' && <span className="scope-tag scope-tag--personal">Л</span>}
                                      <button className="btn-edit" onClick={() => {
                                        setEditingTaskId(t.id);
                                        setEditTask({ title: t.title, priority: t.priority ?? 2, deadline: t.deadline ? new Date(t.deadline) : null });
                                      }}>[✎]</button>
                                      <button className="btn-delete" onClick={() => api.delete(`/api/tasks/${t.id}`).then(loadData)}>[×]</button>
                                    </span>
                                  </div>
                                  {editingTaskId === t.id ? (
                                    <div className="card-edit-form">
                                      <input className="input" value={editTask.title} onChange={e => setEditTask(s => ({...s, title: e.target.value}))} onKeyDown={e => e.key === 'Enter' && saveTask(t.id)} />
                                      <div className="card-edit-row">
                                        <select className="input input--select input--priority" value={editTask.priority} onChange={e => setEditTask(s => ({...s, priority: Number(e.target.value)}))}>
                                          <option value={1}>!! ВЫСОКИЙ</option>
                                          <option value={2}>!  СРЕДНИЙ</option>
                                          <option value={3}>   НИЗКИЙ</option>
                                        </select>
                                        <button className="btn" onClick={() => saveTask(t.id)}>[✓]</button>
                                        <button className="btn" style={{color:'var(--text-dim)'}} onClick={() => setEditingTaskId(null)}>[отмена]</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="kanban-card-title">{t.title}</div>
                                      {t.deadline && (
                                        <div className={`kanban-card-deadline${overdue ? ' overdue' : ''}`}>
                                          ⏰ {formatDeadline(t.deadline)}
                                        </div>
                                      )}
                                    </>
                                  )}
                                  <div className="kanban-card-actions">
                                    <span>
                                      {col.key !== 'general' && (
                                        <button className="move-btn" onClick={() =>
                                          api.put(`/api/tasks/${t.id}/status`, { status: col.key === 'in_progress' ? 'general' : 'in_progress' }).then(loadData)}>◄</button>
                                      )}
                                    </span>
                                    <span>
                                      {col.key !== 'done' && (
                                        <button className="move-btn" onClick={() =>
                                          api.put(`/api/tasks/${t.id}/status`, { status: col.key === 'general' ? 'in_progress' : 'done' }).then(loadData)}>►</button>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                            {tasks.filter(t => t.category === col.key).length === 0 && (
                              <div className="empty">[ ПУСТО ]</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>

                    {/* ── ЗАМЕТКИ ── */}
                    <section className="section">
                      <div className="section-header">
                        <span className="section-title">▸ ЗАМЕТКИ</span>
                        <span className="section-count">[{notes.length}]</span>
                      </div>
                      <div className="add-form">
                        <input className="input" placeholder="ЗАГОЛОВОК"
                          value={newNote.title}
                          onChange={e => setNewNote({ ...newNote, title: e.target.value })}
                          onKeyDown={e => e.key === 'Enter' && addNote()} />
                        <input className="input" placeholder="ТЕКСТ (НЕОБЯЗАТЕЛЬНО)"
                          value={newNote.content}
                          onChange={e => setNewNote({ ...newNote, content: e.target.value })}
                          onKeyDown={e => e.key === 'Enter' && addNote()} />
                        <label className="check-label">
                          <input type="checkbox" checked={newNote.scope === 'personal'}
                            onChange={e => setNewNote(n => ({ ...n, scope: e.target.checked ? 'personal' : 'family' }))} />
                          ЛИЧНОЕ
                        </label>
                        <button className="btn" onClick={addNote}>[+ ДОБАВИТЬ]</button>
                      </div>
                      <div className="notes-grid">
                        {notes.map(n => (
                          <div key={n.id} className={`note-card${n.scope === 'personal' ? ' note-card--personal' : ''}`}>
                            <div className="note-card-top">
                              {n.scope === 'personal' && <span className="scope-tag scope-tag--personal">Л</span>}
                              <span style={{display:'flex',gap:'4px',alignItems:'center',marginLeft:'auto'}}>
                                <button className="btn-edit" onClick={() => {
                                  setEditingNoteId(n.id);
                                  setEditNote({ title: n.title, content: n.content || '' });
                                }}>[✎]</button>
                                <button className="btn-delete" onClick={() => api.delete(`/api/notes/${n.id}`).then(loadData)}>[×]</button>
                              </span>
                            </div>
                            {editingNoteId === n.id ? (
                              <div className="card-edit-form">
                                <input className="input" value={editNote.title} onChange={e => setEditNote(s => ({...s, title: e.target.value}))} onKeyDown={e => e.key === 'Enter' && saveNote(n.id)} />
                                <textarea className="input" rows={3} value={editNote.content} onChange={e => setEditNote(s => ({...s, content: e.target.value}))} style={{resize:'vertical',minHeight:'60px'}} />
                                <div className="card-edit-row">
                                  <button className="btn" onClick={() => saveNote(n.id)}>[✓ СОХРАНИТЬ]</button>
                                  <button className="btn" style={{color:'var(--text-dim)'}} onClick={() => setEditingNoteId(null)}>[отмена]</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="note-title">{n.title}</div>
                                {n.content && <div className="note-content">{n.content}</div>}
                              </>
                            )}
                            <div className="note-date">{formatDate(n.created_at)}</div>
                          </div>
                        ))}
                        {notes.length === 0 && <div className="empty">[ ПУСТО ]</div>}
                      </div>
                    </section>

                    {/* ── СОБЫТИЯ ── */}
                    <section className="section section--events" id="section-events">
                      <div className="section-header">
                        <span className="section-title">▸ СОБЫТИЯ</span>
                        <span className="section-count">[{events.length}]</span>
                        <button className="btn btn--sm" onClick={() => setShowEventForm(f => !f)}>
                          {showEventForm ? '[ЗАКРЫТЬ]' : '[+ ДОБАВИТЬ]'}
                        </button>
                      </div>

                      {showEventForm && (
                        <div className="event-form">
                          <input className="input" placeholder="НАЗВАНИЕ СОБЫТИЯ"
                            value={newEvent.title}
                            onChange={e => setNewEvent(ev => ({ ...ev, title: e.target.value }))} />
                          <div className="event-form-row">
                            <input type="date" className="input input--date-native"
                              value={eventDateRaw}
                              onChange={e => handleEventDateChange(e.target.value)} />
                            <label className="check-label">
                              <input type="checkbox" checked={newEvent.is_yearly}
                                onChange={e => handleEventDateChange(eventDateRaw, e.target.checked)} />
                              ЕЖЕГОДНО
                            </label>
                          </div>
                          <div className="event-form-row">
                            <input type="number" className="input input--year" placeholder="ГОД (необяз.)"
                              min={1900} max={2100}
                              value={newEvent.year}
                              onChange={e => setNewEvent(ev => ({ ...ev, year: e.target.value !== '' ? Number(e.target.value) : '' }))} />
                            <span className="event-year-hint">для возраста/лет</span>
                          </div>
                          <div className="event-form-checks">
                            <label className="check-label">
                              <input type="checkbox" checked={newEvent.notify_30}
                                onChange={e => setNewEvent(ev => ({ ...ev, notify_30: e.target.checked }))} />
                              ЗА 30 ДНЕЙ
                            </label>
                            <label className="check-label">
                              <input type="checkbox" checked={newEvent.notify_7}
                                onChange={e => setNewEvent(ev => ({ ...ev, notify_7: e.target.checked }))} />
                              ЗА 7 ДНЕЙ
                            </label>
                            <label className="check-label">
                              <input type="checkbox" checked={newEvent.notify_1}
                                onChange={e => setNewEvent(ev => ({ ...ev, notify_1: e.target.checked }))} />
                              ЗА 1 ДЕНЬ
                            </label>
                            <label className="check-label check-label--orange">
                              <input type="checkbox" checked={newEvent.create_task}
                                onChange={e => setNewEvent(ev => ({ ...ev, create_task: e.target.checked }))} />
                              СОЗДАТЬ ЗАДАЧУ
                            </label>
                            <label className="check-label check-label--personal">
                              <input type="checkbox" checked={newEvent.scope === 'personal'}
                                onChange={e => setNewEvent(ev => ({ ...ev, scope: e.target.checked ? 'personal' : 'family' }))} />
                              ЛИЧНОЕ
                            </label>
                          </div>
                          {eventError && <div className="event-error">{eventError}</div>}
                          <div className="event-form-actions">
                            <button className="btn" onClick={addEvent}>[ДОБАВИТЬ]</button>
                            <button className="btn btn--dim" onClick={() => {
                              setShowEventForm(false); setEventDateRaw(''); setEventError('');
                              setNewEvent({ title:'', date:'', is_yearly:true, notify_30:true, notify_7:true, notify_1:true, create_task:false, year:'', scope:'family' });
                            }}>[ОТМЕНА]</button>
                          </div>
                        </div>
                      )}

                      {(() => {
                        const weekEvs  = sortedEvents.filter(ev => { const d = daysUntilEvent(ev); return d >= 0 && d <= 7; });
                        const monthEvs = sortedEvents.filter(ev => { const d = daysUntilEvent(ev); return d > 7 && d <= 31; });
                        const restEvs  = sortedEvents.filter(ev => daysUntilEvent(ev) > 31 || daysUntilEvent(ev) < 0);

                        const renderEv = (ev: CalEvent) => {
                          const days = daysUntilEvent(ev);
                          const isPast = days < 0;
                          const age = getEventAge(ev);
                          const isEditing = editingId === ev.id;
                          return (
                            <div key={ev.id}>
                              <div className={`event-row${isPast ? ' event-row--past' : ''}`}>
                                <div className="event-row-btns">
                                  <button className="btn-edit" onClick={() => isEditing ? setEditingId(null) : startEdit(ev)}>[✎]</button>
                                  <button className="btn-delete" onClick={() => api.delete(`/api/events/${ev.id}`).then(loadData)}>[×]</button>
                                </div>
                                <div className="event-row-main">
                                  <span className="event-date-label">{formatEventDate(ev)}{ev.is_yearly ? '' : ' (разовое)'}</span>
                                  <span className="event-title">{ev.title}</span>
                                  {ev.scope === 'personal' && <span className="scope-tag scope-tag--personal">Л</span>}
                                  {age && <span className="event-age">{age}</span>}
                                </div>
                                <div className="event-row-meta">
                                  {days >= 0
                                    ? <span className="event-days">{days === 0 ? 'СЕГОДНЯ' : days === 1 ? 'ЗАВТРА' : `${days} ДН.`}</span>
                                    : <span className="event-days event-days--past">ПРОШЛО</span>
                                  }
                                  <span className="event-badges">
                                    {ev.notify_30 && <span className="badge">30</span>}
                                    {ev.notify_7  && <span className="badge">7</span>}
                                    {ev.notify_1  && <span className="badge">1</span>}
                                    {ev.create_task ? <span className="badge badge--orange">✓ЗАДАЧА</span> : null}
                                  </span>
                                </div>
                              </div>
                              {isEditing && (
                                <div className="event-edit-form">
                                  <input className="input" placeholder="НАЗВАНИЕ"
                                    value={editForm.title}
                                    onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
                                  <div className="event-form-row">
                                    <input type="date" className="input input--date-native"
                                      value={editDateRaw}
                                      onChange={e => handleEditDateChange(e.target.value)} />
                                    <label className="check-label">
                                      <input type="checkbox" checked={editForm.is_yearly}
                                        onChange={e => handleEditDateChange(editDateRaw, e.target.checked)} />
                                      ЕЖЕГОДНО
                                    </label>
                                  </div>
                                  <div className="event-form-row">
                                    <input type="number" className="input input--year" placeholder="ГОД"
                                      min={1900} max={2100}
                                      value={editForm.year}
                                      onChange={e => setEditForm(f => ({ ...f, year: e.target.value !== '' ? Number(e.target.value) : '' }))} />
                                    <span className="event-year-hint">для возраста/лет</span>
                                  </div>
                                  <div className="event-form-checks">
                                    <label className="check-label">
                                      <input type="checkbox" checked={editForm.notify_30}
                                        onChange={e => setEditForm(f => ({ ...f, notify_30: e.target.checked }))} />30 ДН.
                                    </label>
                                    <label className="check-label">
                                      <input type="checkbox" checked={editForm.notify_7}
                                        onChange={e => setEditForm(f => ({ ...f, notify_7: e.target.checked }))} />7 ДН.
                                    </label>
                                    <label className="check-label">
                                      <input type="checkbox" checked={editForm.notify_1}
                                        onChange={e => setEditForm(f => ({ ...f, notify_1: e.target.checked }))} />1 ДН.
                                    </label>
                                    <label className="check-label check-label--orange">
                                      <input type="checkbox" checked={editForm.create_task}
                                        onChange={e => setEditForm(f => ({ ...f, create_task: e.target.checked }))} />ЗАДАЧА
                                    </label>
                                  </div>
                                  {editError && <div className="event-error">{editError}</div>}
                                  <div className="event-form-actions">
                                    <button className="btn" onClick={() => saveEdit(ev.id)}>[СОХРАНИТЬ]</button>
                                    <button className="btn btn--dim" onClick={() => setEditingId(null)}>[ОТМЕНА]</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        };

                        return (
                          <div className="events-layout">
                          <div className="cal-wrap cal-wrap--mini">
                            <div className="cal-nav">
                              <button className="cal-nav-btn" onClick={prevMonth}>◄</button>
                              <span className="cal-month-label">{MONTHS_RU[calMonth]} {calYear}</span>
                              <button className="cal-nav-btn" onClick={nextMonth}>►</button>
                            </div>
                            <div className="cal-grid">
                              {WEEKDAYS.map(wd => <div key={wd} className="cal-header">{wd}</div>)}
                              {calCells.map((day, i) => {
                                if (day === null) return <div key={`e2${i}`} className="cal-cell cal-cell--empty" />;
                                const dateKey = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                                const isToday = dateKey === todayKey;
                                const dayEvs = eventsForDay(events, calYear, calMonth, day);
                                return (
                                  <div key={`d2${day}`}
                                    className={`cal-cell${isToday ? ' cal-cell--today' : ''}${dayEvs.length > 0 ? ' cal-cell--has-event' : ''}`}
                                    title={dayEvs.map(e => e.title).join(', ')}
                                    onClick={() => {
                                      setShowEventForm(true);
                                      const y=calYear, m=String(calMonth+1).padStart(2,'0'), d=String(day).padStart(2,'0');
                                      setEventDateRaw(`${y}-${m}-${d}`);
                                      handleEventDateChange(`${y}-${m}-${d}`);
                                    }}>
                                    <span className="cal-day-num">{day}</span>
                                    {dayEvs.length > 0 && <span className="cal-dot" />}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="events-list">
                            {events.length === 0 && <div className="empty">[ НЕТ СОБЫТИЙ ]</div>}

                            {weekEvs.length > 0 && (
                              <div className="ev-group">
                                <div className="ev-group-label">▸ ЭТА НЕДЕЛЯ [{weekEvs.length}]</div>
                                {weekEvs.map(renderEv)}
                              </div>
                            )}

                            {monthEvs.length > 0 && (
                              <div className="ev-group">
                                <div className="ev-group-label">▸ ЭТОТ МЕСЯЦ [{monthEvs.length}]</div>
                                {monthEvs.map(renderEv)}
                              </div>
                            )}

                            {restEvs.length > 0 && (
                              <div className="ev-group">
                                <button className="ev-expand-btn" onClick={() => setEventsExpanded(e => !e)}>
                                  {eventsExpanded ? '▾' : '▸'} ЕЩЁ {restEvs.length} {restEvs.length === 1 ? 'СОБЫТИЕ' : restEvs.length < 5 ? 'СОБЫТИЯ' : 'СОБЫТИЙ'}
                                </button>
                                {eventsExpanded && restEvs.map(renderEv)}
                              </div>
                            )}
                          </div>
                          </div>
                        );
                      })()}
                    </section>

                    {/* ── СЕМЬЯ ── */}
                    <section className="section section--family">
                      <div className="section-header">
                        <span className="section-title">▸ СЕМЬЯ</span>
                        <span className="section-count">[{members.length}]</span>
                        {me?.is_admin && (
                          <button className="btn btn--sm" onClick={createInvite}>[+ ИНВАЙТ]</button>
                        )}
                      </div>

                      {newInviteLink && (
                        <div className="invite-box">
                          <div className="invite-label">🔗 ССЫЛКА-ПРИГЛАШЕНИЕ (24 ЧАСА):</div>
                          <code className="invite-link">{newInviteLink}</code>
                          <button className="btn btn--sm" onClick={() => navigator.clipboard.writeText(newInviteLink)}>
                            [КОПИРОВАТЬ]
                          </button>
                        </div>
                      )}
                      {familyError && <div className="event-error">{familyError}</div>}

                      <div className="family-members">
                        {members.map(m => (
                          <div key={m.user_id} className="member-row">
                            <span className="member-icon">{m.role === 'owner' ? '👑' : '👤'}</span>
                            <div className="member-info">
                              <span className="member-name">{m.display_name}</span>
                              <span className="member-since">с {m.joined_at?.slice(0, 10)}</span>
                            </div>
                            <span className="member-role">{m.role === 'owner' ? '[ВЛАДЕЛЕЦ]' : '[УЧАСТНИК]'}</span>
                            {me?.is_admin && m.role !== 'owner' && (
                              <button className="btn-delete" onClick={() => removeMember(m.user_id)}>[×]</button>
                            )}
                          </div>
                        ))}
                      </div>

                      {me?.is_admin && invites.length > 0 && (
                        <div className="invites-list">
                          <div className="invites-title">АКТИВНЫЕ ИНВАЙТЫ:</div>
                          {invites.filter(inv => !inv.used_by).map(inv => (
                            <div key={inv.code} className="invite-row">
                              <span className="invite-code">{inv.code}</span>
                              <span className="invite-expires">до {inv.expires_at.slice(0, 16).replace('T', ' ')}</span>
                              <button className="btn-delete" onClick={() => revokeInvite(inv.code)}>[отозвать]</button>
                            </div>
                          ))}
                          {invites.filter(inv => inv.used_by).map(inv => (
                            <div key={inv.code} className="invite-row invite-row--used">
                              <span className="invite-code">{inv.code}</span>
                              <span className="invite-expires">использован {inv.used_at?.slice(0, 10)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>

                  </main>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* ── Nixie clock panel ── */}
        <div className="bottom-panel">
          <div className="brand">
            <div className="brand-name">VAULT-TEC INDUSTRIES</div>
            <div className="brand-model">FAMILY TERMINAL</div>
          </div>
          <div className="nixie-clock">
            <div className="nx-tube"><span className="nx-digit">{nixieTime[0]}</span></div>
            <div className="nx-tube"><span className="nx-digit">{nixieTime[1]}</span></div>
            <div className={`nx-sep${nixieBlink ? '' : ' nx-sep--dim'}`}>
              <div className="nx-dot"/><div className="nx-dot"/>
            </div>
            <div className="nx-tube"><span className="nx-digit">{nixieTime[2]}</span></div>
            <div className="nx-tube"><span className="nx-digit">{nixieTime[3]}</span></div>
          </div>
          <div className="power-area">
            <span className="power-led">●</span>
            <span className="power-lbl">POWER</span>
          </div>
        </div>

      </div>
    </div>
  );
}

export default App;
